import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { TableWrapper, TableHead, FilterRow } from '../components/TableWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { traceabilityApi, type TraceReport, type TraceSection } from '../services/traceability';

function renderValue(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
}

function TraceSectionTable({ section }: { section: TraceSection }) {
  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold">{section.title}</h3>
      {section.rows.length === 0 ? (
        <p className="text-sm text-gray-500">{section.emptyText}</p>
      ) : (
        <TableWrapper maxHeight="none">
          <TableHead>
            <tr>
              {section.columns.map((column) => (
                <th key={column.key} className="px-2 py-1.5 text-left font-medium">
                  {column.label}
                </th>
              ))}
            </tr>
            <FilterRow>
              <td colSpan={section.columns.length} className="px-2 py-1" />
            </FilterRow>
          </TableHead>
          <tbody>
            {section.rows.map((row, index) => (
              <tr key={`${section.title}-${index}`} className="border-b border-gray-100">
                {section.columns.map((column) => (
                  <td key={column.key} className="px-2 py-1.5 align-top">
                    {renderValue(row[column.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </TableWrapper>
      )}
    </div>
  );
}

export function TraceabilityPage() {
  const { canRead } = useAuth();
  const { error: toastError, info: toastInfo } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') ?? '');
  const [report, setReport] = useState<TraceReport | null>(null);
  const [loading, setLoading] = useState(false);

  if (!canRead('traceability')) {
    return <Navigate to="/" replace />;
  }

  const activeQuery = useMemo(() => (searchParams.get('q') ?? '').trim(), [searchParams]);

  useEffect(() => {
    if (activeQuery === '') {
      setReport(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    traceabilityApi.search(activeQuery)
      .then((result) => {
        if (!cancelled) {
          setReport(result);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setReport(null);
          toastError(error instanceof Error ? error.message : 'Kunde inte söka spårning');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [activeQuery, toastError]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    const next = query.trim();
    if (next === '') {
      toastInfo('Ange ett S-, L-, H-, T- eller I-nummer');
      return;
    }
    setSearchParams({ q: next });
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-semibold">Spårning</h1>
        <p className="text-sm text-gray-600">Sök på S, L, H, T eller I och följ flödet bakåt och framåt.</p>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="T26.9, L2026.0001, H200..."
          className="w-full max-w-md"
        />
        <button type="submit" className="rounded border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50">
          Sök
        </button>
      </form>

      {loading && (
        <p className="text-sm text-gray-500">Söker...</p>
      )}

      {!loading && report === null && activeQuery === '' && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-sm text-gray-600">
          Börja med att söka på ett nummer, till exempel `T26.9` eller `L2026.0001`.
        </div>
      )}

      {!loading && report && (
        <div className="space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="text-base font-semibold">{report.title}</h2>
            <p className="mt-1 text-sm text-gray-600">{report.subtitle}</p>
            <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {report.overview.map((item) => (
                <div key={`${item.label}-${item.value}`} className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                  <div className="text-xs uppercase tracking-wide text-gray-500">{item.label}</div>
                  <div className="mt-1 text-sm font-medium">{item.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="text-base font-semibold">Bakåt i kedjan</h2>
              {report.upstreamSections.length === 0 ? (
                <p className="text-sm text-gray-500">Ingen tidigare kedja hittades för denna sökning.</p>
              ) : (
                report.upstreamSections.map((section) => (
                  <TraceSectionTable key={section.title} section={section} />
                ))
              )}
            </div>

            <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4">
              <h2 className="text-base font-semibold">Framåt i kedjan</h2>
              {report.downstreamSections.length === 0 ? (
                <p className="text-sm text-gray-500">Ingen senare kedja hittades för denna sökning.</p>
              ) : (
                report.downstreamSections.map((section) => (
                  <TraceSectionTable key={section.title} section={section} />
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
